<script module lang="ts">
	import type { Component } from 'svelte';
	interface ChecklistItem {
		passed: boolean;
		significant: boolean;
		key: string;
		value: number;
		description: string;
		tooltipComponent?: Component;
	}
</script>

<script lang="ts">
	import { Frown, Info, Meh, Smile } from 'lucide-svelte';
	import InfoDialog from './InfoDialog.svelte';
	import NetPropertyIncomeYieldTooltip from './NetPropertyIncomeYieldTooltip.svelte';
	import { valueComparisonAdjective } from './utils';

	interface Props {
		quote: ReitQuote;
	}

	let { quote }: Props = $props();
	let fundamental: ChecklistItem[] = $state([]);
	let financial: ChecklistItem[] = $state([]);
	let valuation: ChecklistItem[] = $state([]);

	$effect(() => {
		if (quote) {
			const newFundamental: ChecklistItem[] = [];
			const newFinancial: ChecklistItem[] = [];
			const newValuation: ChecklistItem[] = [];
			if (quote.sectorOccupancy)
				newFundamental.push({
					passed: quote.realEstateInvestmentTrust.occupancyRate > quote.sectorOccupancy,
					significant:
						Math.abs(quote.realEstateInvestmentTrust.occupancyRate - quote.sectorOccupancy) >= 1,
					key:
						quote.realEstateInvestmentTrust.reitTheme.uid === 'hospitality-reits'
							? 'Change in REVPAR'
							: 'Occupancy Rate',
					value: quote.realEstateInvestmentTrust.occupancyRate,
					description: `of ${quote.realEstateInvestmentTrust.occupancyRate?.toFixed(1)}% is ${valueComparisonAdjective(
						quote.realEstateInvestmentTrust.occupancyRate,
						quote.sectorOccupancy
					)} sector average of ${quote.sectorOccupancy?.toFixed(1)}%`
				});
			if (quote.realEstateInvestmentTrust.npiYield)
				newFundamental.push({
					passed: quote.realEstateInvestmentTrust.npiYield > quote.dividendYield * 100,
					significant:
						Math.abs(quote.realEstateInvestmentTrust.npiYield - quote.dividendYield * 100) >= 0.01,
					key: 'Net property income yield',
					value: quote.realEstateInvestmentTrust.npiYield,
					description: `of ${quote.realEstateInvestmentTrust.npiYield.toFixed(2)}% is ${valueComparisonAdjective(
						quote.realEstateInvestmentTrust.npiYield,
						quote.dividendYield * 100
					)} its dividend yield of ${quote.dividendYield.toLocaleString('default', {
						style: 'percent',
						maximumFractionDigits: 1
					})}`,
					tooltipComponent: NetPropertyIncomeYieldTooltip
				});
			if (quote.sectorGearing)
				newFinancial.push({
					passed: quote.sectorGearing - quote.gearingRatio > 0.01,
					significant: Math.abs(quote.gearingRatio - quote.sectorGearing) >= 0.01,
					key: 'Gearing Ratio',
					value: quote.gearingRatio,
					description: `of ${quote.gearingRatio.toFixed(2)}x is ${valueComparisonAdjective(
						quote.gearingRatio,
						quote.sectorGearing
					)} sector average of ${quote.sectorGearing.toFixed(2)}x`
				});
			if (quote.realEstateInvestmentTrust.interestCoverageRatio)
				newFinancial.push({
					passed: quote.realEstateInvestmentTrust.interestCoverageRatio > 2.5,
					significant: Math.abs(quote.realEstateInvestmentTrust.interestCoverageRatio - 2.5) >= 0.1,
					key: 'Interest Coverage Ratio',
					value: quote.realEstateInvestmentTrust.interestCoverageRatio,
					description: `of ${quote.realEstateInvestmentTrust.interestCoverageRatio.toFixed(1)}x is ${
						quote.realEstateInvestmentTrust.interestCoverageRatio > 2.5 ? 'above' : 'below'
					} 2.5x`
				});
			if (
				quote.realEstateInvestmentTrust.currentNetBookValue &&
				quote.realEstateInvestmentTrust.previousNetBookValue
			)
				newFinancial.push({
					passed:
						quote.realEstateInvestmentTrust.currentNetBookValue >
						quote.realEstateInvestmentTrust.previousNetBookValue,
					significant:
						Math.abs(
							quote.realEstateInvestmentTrust.currentNetBookValue -
								quote.realEstateInvestmentTrust.previousNetBookValue
						) >= 0.01,
					key: 'Net Book Value',
					value: quote.realEstateInvestmentTrust.currentNetBookValue,
					description: `of ${quote.realEstateInvestmentTrust.currentNetBookValue.toLocaleString(
						'default',
						{
							style: 'currency',
							currency: quote.realEstateInvestmentTrust.symbol.currency,
							currencyDisplay: 'narrowSymbol'
						}
					)} is ${valueComparisonAdjective(quote.realEstateInvestmentTrust.currentNetBookValue, quote.realEstateInvestmentTrust.previousNetBookValue)} its net book value of ${quote.realEstateInvestmentTrust.previousNetBookValue.toLocaleString(
						'default',
						{
							style: 'currency',
							currency: quote.realEstateInvestmentTrust.symbol.currency,
							currencyDisplay: 'narrowSymbol'
						}
					)} in the previous year`
				});
			if (quote.sectorYield)
				newValuation.push({
					passed: quote.dividendYield > quote.sectorYield,
					significant: Math.abs(quote.dividendYield - quote.sectorYield) >= 0.002,
					key: 'Dividend Yield',
					value: quote.dividendYield,
					description: `of ${quote.dividendYield.toLocaleString('default', {
						style: 'percent',
						maximumFractionDigits: 1
					})} is ${valueComparisonAdjective(quote.dividendYield, quote.sectorYield)} sector average of ${quote.sectorYield.toLocaleString(
						'default',
						{
							style: 'percent',
							maximumFractionDigits: 1
						}
					)}`
				});
			if (quote.realEstateInvestmentTrust.dpuGrowth)
				newValuation.push({
					passed: quote.realEstateInvestmentTrust.dpuGrowth > 0,
					significant: Math.abs(quote.realEstateInvestmentTrust.dpuGrowth) >= 0.01,
					key: 'Distribution per unit growth',
					value: quote.realEstateInvestmentTrust.dpuGrowth,
					description: `is ${quote.realEstateInvestmentTrust.dpuGrowth.toFixed(2)}%`
				});
			if (quote.sectorPriceToBook)
				newValuation.push({
					passed: quote.priceToBook < quote.sectorPriceToBook,
					significant: Math.abs(quote.priceToBook - quote.sectorPriceToBook) >= 0.1,
					key: 'Price to Book',
					value: quote.priceToBook,
					description: `of ${quote.priceToBook?.toFixed(2)}x is ${valueComparisonAdjective(quote.priceToBook, quote.sectorPriceToBook)} sector average of ${quote.sectorPriceToBook.toFixed(2)}x`
				});
			fundamental = newFundamental;
			financial = newFinancial;
			valuation = newValuation;
		}
	});
</script>

<div class="divide-gray-faint @container divide-y">
	{#each [{ title: 'Fundamental', list: fundamental }, { title: 'Financial', list: financial }, { title: 'Valuation', list: valuation }] as { title, list }}
		<section
			class="[&:not(:first-child)]:pt-3 lg:[&:not(:first-child)]:pt-6 [&:not(:last-child)]:pb-3 lg:[&:not(:last-child)]:pb-6"
		>
			<header class="body-bold mb-3">{title}</header>
			<ul class="grid gap-3">
				{#each list as { passed, value, significant, key, description, tooltipComponent }}
					<li class="flex gap-1 leading-7 @xs:gap-2">
						<div class="flex @lg:items-center">
							{#if value && significant}
								{#if passed}
									<Smile class="text-green-ticker w-5" />
								{:else}
									<Frown class="text-red-ticker w-5" />
								{/if}
							{:else}
								<Meh class="text-gray-mid w-5" />
							{/if}
						</div>

						<p class="">
							{#if value}
								<span class="font-medium">{key}</span> {description}
							{:else}
								No {key} data available
							{/if}
							{#if tooltipComponent}
								<InfoDialog>
									{#snippet triggerChildren()}
										<Info class="text-gray-mid h-3 w-3" />
									{/snippet}
									{#snippet contentChildren()}
										{@const SvelteComponent = tooltipComponent}

										<SvelteComponent />
									{/snippet}
								</InfoDialog>
							{/if}
						</p>
					</li>
				{/each}
			</ul>
		</section>
	{/each}
</div>
